import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { StorageDocumentLink } from '../../../../components/storage-document-link/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useSelected } from '../../../../hooks/company/index';
import { useFormat } from '../../../../hooks/index';
import { SafeConversionSummaryResponse } from '../../../../hooks/safe/index';
import { InvestmentRoundingSettingTitle } from '../../../../types/investment-rounding.types';
import { downloadCompanyStorageFile } from '../../../../utils/company-storage-url.util';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { ConvertSafeItem } from '../conversion-summary/convert-safe-item';
import { ChildrenFormProps } from '../convert-modal.types';

export type FinalStepProps = ChildrenFormProps;

export const FinalStep: FC<FinalStepProps> = ({
  companyId,
  nextFormStep,
  handleCloseModal,
  formData,
  prevFormStep,
  isLoading,
}) => {
  const { selectedCompany } = useSelected();
  const { stepOne, stepThree } = formData();
  const { format } = useFormat();
  const handleDocumentClick = (fileLink: string) => {
    void downloadCompanyStorageFile(companyId, fileLink);
  };

  const findSafeFiles = (safeId: string) => {
    return stepOne.safes.find((safe) => safe.id === safeId)?.files || [];
  };
  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="GENERAL" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="CONVERSION_DATE" />}
              value={format(stepOne?.conversionDate, 'dd/MM/yyyy')}
            />
            <SummaryItem
              title={<AppFormattedMessage id="LINKED_EVENT" />}
              value={stepOne?.linkedEvent?.name}
            />
            <SummaryItem
              title={<AppFormattedMessage id="SHARE_CLASS" />}
              value={stepOne?.shareClass?.name}
            />
            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="ROUNDING_SETTINGS" />}
              value={
                stepOne?.roundingSetting ? (
                  <AppFormattedMessage
                    id={InvestmentRoundingSettingTitle[stepOne.roundingSetting]}
                  />
                ) : (
                  '-'
                )
              }
            />
          </div>
        </div>

        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="SELECTED_SAFES" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4 py-1">
            {stepOne?.safes?.map((safe) => (
              <SummaryItem
                key={safe.id}
                title={
                  <Tooltip content={safe?.stakeholder?.fullName}>
                    {safe?.stakeholder?.fullName}
                  </Tooltip>
                }
                titleClassName="truncate w-[220px]"
                value={`${selectedCompany?.currency?.symbol} ${safe?.investment.toLocaleString('en-US')}, ${format(safe?.issueDate, 'dd/MM/yyyy')}`}
              />
            ))}
          </div>
        </div>

        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="GENERAL_CONVERSION_SUMMARY" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4 py-1">
            <SummaryItem
              title={<AppFormattedMessage id="EVENT_SHARE_PRICE_POST_CONVERSION" />}
              value={`${selectedCompany?.currency?.symbol} ${toMathRound(stepOne?.conversionSummary?.sharePrice)}`}
            />
            <SummaryItem
              title={<AppFormattedMessage id="SAFE_INVESTMENT_CONVERSION_SUM" />}
              value={`${selectedCompany?.currency?.symbol} ${stepOne?.conversionSummary?.investment?.toLocaleString('en-US')}`}
            />
            <SummaryItem
              title={<AppFormattedMessage id="PRE_MONEY_VALUATION" />}
              value={`${selectedCompany?.currency?.symbol} ${stepOne?.conversionSummary?.preMoneyValuation?.toLocaleString('en-US')}`}
            />
            <SummaryItem
              title={<AppFormattedMessage id="TOTAL_PERCENT_OWNED_SAFE" />}
              value={` ${stepOne?.conversionSummary?.totalOwned}%`}
            />
            <SummaryItem
              title={<AppFormattedMessage id="CONVERTED_SHARES" />}
              value={toRound(stepOne?.conversionSummary?.convertedShares, 0).toLocaleString(
                'en-US',
              )}
            />
          </div>
        </div>

        <div className="mt-2 flex w-full flex-col gap-4 rounded-lg p-4 shadow-sm">
          <span className="text-label-md font-[500] text-gray-500">
            <AppFormattedMessage id="CONVERSION_DETAILS" />
          </span>
          <div className="flex w-full flex-col gap-2">
            {stepOne?.conversionDetails?.map(({ id: safeId, summary }) => (
              <ConvertSafeItem
                currencySign={selectedCompany?.currency?.symbol}
                documents={findSafeFiles(safeId)}
                key={safeId}
                onDocumentClick={handleDocumentClick}
                safeId={safeId}
                summary={summary as SafeConversionSummaryResponse}
              />
            ))}
          </div>
        </div>

        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className={'flex gap-3 text-nowrap px-4 py-2'}>
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="DOCUMENTS" />:
            </span>
            <div className="flex max-w-[220px] flex-col gap-2">
              {stepThree?.files?.some(({ docLink }) => docLink) ? (
                stepThree.files.map(
                  ({ doc, docLink }) =>
                    docLink && (
                      <StorageDocumentLink
                        key={docLink + doc.name}
                        label={doc.name}
                        link={docLink}
                        onDocumentClick={handleDocumentClick}
                      />
                    ),
                )
              ) : (
                <span className="truncate text-xs font-[450] text-gray-700">-</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className="flex w-full gap-3 px-4 py-2">
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div className="max-w-[250px] break-words text-xs font-[450] text-gray-700">
              {stepThree?.additionalNotes || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>

          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="CONVERT" />
          </Button>
        </div>
      </div>
    </>
  );
};
